import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase/config';
import { getAdminLeads } from '../../api/adminService';
import './CustomerLeadsPage.css';

const SOURCE_LABELS = {
  meta_if: 'Meta 광고',
  pre_register: '사전예약',
  group_inquiry: '단체문의',
  school_trial: '학교 무료체험',
  landing_page: '랜딩',
  bulk_purchase: '대량구매',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat('ko-KR', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const csvEscape = (value) => {
  const text = String(value ?? '');
  return `"${text.replace(/"/g, '""')}"`;
};

const getSourceLabel = (source) => SOURCE_LABELS[source] || source || '출처 없음';

const PLAN_LABELS = {
  '1month': '1개월',
  '3month': '3개월',
  '6month': '6개월',
  '12month': '12개월',
};

const SEASON_30_PRICES = {
  '1month': 24000,
  '3month': 51000,
  '6month': 82500,
  '12month': 140000,
};

const SEASON_50_PRICES = {
  '1month': 24000,
  '3month': 49000,
  '6month': 69000,
  '12month': 99000,
};

const STANDARD_PRICES = {
  '1month': 24000,
  '3month': 60000,
  '6month': 110000,
  '12month': 200000,
};

const formatWon = (amount) => {
  const number = Number(amount || 0);
  return number ? `${Math.round(number).toLocaleString()}원` : '';
};

const priceLine = (prices) => (
  ['1month', '3month', '6month', '12month']
    .filter((planId) => prices[planId])
    .map((planId) => `${PLAN_LABELS[planId]} ${formatWon(prices[planId])}`)
    .join(' · ')
);

const normalizePlan = (value) => {
  const raw = String(value || '').toLowerCase().replace('pro_', '');
  if (PLAN_LABELS[raw]) return raw;
  if (raw.includes('12month') || raw.includes('12개월')) return '12month';
  if (raw.includes('6month') || raw.includes('6개월')) return '6month';
  if (raw.includes('3month') || raw.includes('3개월')) return '3month';
  if (raw.includes('1month') || raw.includes('1개월')) return '1month';
  return '';
};

const extractQuantity = (row) => {
  const direct = Number(row.bulk_purchase_quantity || row.quantity || row.teacher_count || 0);
  if (direct > 0) return direct;
  const notes = String(row.notes || '');
  const bulkMatch = notes.match(/[×x]\s*(\d+)\s*개/);
  if (bulkMatch) return Number(bulkMatch[1]);
  const qtyMatch = notes.match(/수량:\s*(\d+)/);
  if (qtyMatch) return Number(qtyMatch[1]);
  return 1;
};

const getLeadPriceContext = (row) => {
  if (row.price_context?.price) return row.price_context;

  const source = row.source || '';
  const formName = row.form_name || '';
  const displaySource = row.display_source || getSourceLabel(source);
  const notes = row.notes || '';
  const planId = normalizePlan(row.selected_plan) || normalizePlan(notes);
  const quantity = extractQuantity(row);
  const normalizedForm = formName.toLowerCase();

  if (source === 'bulk_purchase') {
    const paidAmount = Number(row.paid_amount || 0);
    const planLabel = PLAN_LABELS[planId] || '플랜';
    return {
      headline: `${planLabel} × ${quantity}개`,
      price: formatWon(paidAmount) || '결제금액 확인 필요',
      detail: paidAmount ? `1개당 ${formatWon(paidAmount / Math.max(quantity, 1))}` : '대량구매 결제 완료',
    };
  }

  if (source === 'school_trial') {
    return {
      headline: '학교 30일 무료체험',
      price: '무료',
      detail: '검수 후 학교 전체 쿠폰 발급',
    };
  }

  if (source === 'pre_register' && normalizedForm.includes('exhibition_booth')) {
    return {
      headline: '전시회 현장 혜택',
      price: '30일 무료 + 비밀가',
      detail: priceLine(SEASON_50_PRICES),
    };
  }

  if (source === 'pre_register' && normalizedForm.includes('free_training')) {
    return {
      headline: '무료 연수 신청',
      price: '무료',
      detail: '10명 이상 학교 방문 연수',
    };
  }

  if (source === 'pre_register' && planId) {
    const prices = normalizedForm.includes('assessment_2026_may') ? SEASON_30_PRICES : SEASON_50_PRICES;
    const couponText = String(row.coupon_code || '').toUpperCase() === 'SPRING14'
      ? 'SPRING14는 14일 무료체험 쿠폰'
      : row.coupon_code ? `쿠폰 ${row.coupon_code}` : '';
    return {
      headline: `${displaySource} · ${PLAN_LABELS[planId] || row.selected_plan}`,
      price: formatWon(prices[planId]),
      detail: [priceLine(prices), couponText].filter(Boolean).join(' · '),
    };
  }

  if (source === 'meta_if') {
    return {
      headline: displaySource,
      price: '가격 정보 없음',
      detail: '광고 양식에 플랜/금액 필드 없음',
    };
  }

  if (planId) {
    return {
      headline: PLAN_LABELS[planId] || row.selected_plan,
      price: formatWon(STANDARD_PRICES[planId]),
      detail: '정가 기준',
    };
  }

  return {
    headline: displaySource || '신청 맥락 없음',
    price: '가격 정보 없음',
    detail: notes || '',
  };
};

const CustomerLeadsPage = () => {
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    q: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const allRows = useMemo(() => data?.leads || [], [data]);
  const rows = useMemo(() => {
    const query = filters.q.trim().toLowerCase();
    if (!query) return allRows;

    const searchableFields = [
      'phone',
      'email',
      'name',
      'organization',
      'display_source',
      'form_name',
      'selected_plan',
      'price_context',
      'coupon_code',
      'utm_source',
      'utm_campaign',
      'utm_content',
      'landing_page',
      'notes',
    ];

    return allRows.filter((row) => {
      const priceContext = getLeadPriceContext(row);
      const priceText = [priceContext.headline, priceContext.price, priceContext.detail].join(' ');
      return [
        ...searchableFields.map((field) => String(row[field] || '')),
        priceText,
      ].join(' ').toLowerCase().includes(query);
    });
  }, [allRows, filters.q]);

  const loadLeads = async () => {
    if (!user) return;
    setIsLoading(true);
    setError('');

    try {
      const result = await getAdminLeads({
        limit: 2000,
        fetch_limit: 2000,
      });
      setData(result);
    } catch (err) {
      setError(err.message || '고객 리드를 불러오지 못했어요.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsLoading(false);
      return;
    }
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = (event) => {
    event.preventDefault();
  };

  const handleDownloadCsv = () => {
    const header = [
      '등록일',
      '유입',
      '이름',
      '학교/기관',
      '이메일',
      '전화번호',
      '상품',
      '안내 가격',
      '폼',
      'UTM Source',
      'UTM Campaign',
      'UTM Content',
      '쿠폰',
      '메모',
    ];

    const csvRows = rows.map((row) => [
      formatDateTime(row.created_at),
      row.display_source || getSourceLabel(row.source),
      row.name,
      row.organization,
      row.email,
      row.phone,
      row.selected_plan,
      (() => {
        const priceContext = getLeadPriceContext(row);
        return `${priceContext.headline} / ${priceContext.price} / ${priceContext.detail}`;
      })(),
      row.form_name,
      row.utm_source,
      row.utm_campaign,
      row.utm_content,
      row.coupon_code,
      row.notes,
    ]);

    const csv = [
      header.map(csvEscape).join(','),
      ...csvRows.map((row) => row.map(csvEscape).join(',')),
    ].join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `whatquiz-customer-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (authLoading || isLoading) {
    return (
      <div className="customer-leads-page">
        <div className="customer-leads-panel">리드 목록을 불러오는 중</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="customer-leads-page">
        <div className="customer-leads-panel">
          <h1>로그인이 필요해요</h1>
          <p>관리자 계정으로 로그인한 뒤 다시 열어주세요.</p>
          <button type="button" onClick={() => navigate('/auth')}>로그인하기</button>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-leads-page">
      <Helmet>
        <title>고객 리드 현황 | 왓퀴즈 Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="customer-leads-container">
        <header className="customer-leads-header">
          <button type="button" className="customer-leads-back" onClick={() => navigate('/admin')}>
            관리자 홈
          </button>
          <div>
            <p className="customer-leads-eyebrow">Customer Leads</p>
            <h1>고객 찾기</h1>
            <p>전화나 메일이 왔을 때 이름, 학교, 이메일, 전화번호로 바로 찾습니다.</p>
          </div>
          <div className="customer-leads-actions">
            <button type="button" onClick={loadLeads}>새로고침</button>
            <button type="button" onClick={handleDownloadCsv} disabled={!rows.length}>
              CSV 받기
            </button>
          </div>
        </header>

        {error && <div className="customer-leads-error">{error}</div>}

        <section className="customer-leads-filter-card">
          <form className="customer-leads-filters" onSubmit={handleSearch}>
            <label>
              <span>빠른 검색</span>
              <input
                type="search"
                value={filters.q}
                onChange={(event) => handleFilterChange('q', event.target.value)}
                placeholder="전화번호, 이메일, 이름, 학교명을 입력하세요"
                autoFocus
              />
            </label>
            <button type="submit">검색</button>
          </form>
        </section>

        <section className="customer-leads-table-card">
          <div className="customer-leads-table-header">
            <h2>검색 결과 {rows.length}건</h2>
            <p>같은 이메일도 신청 이력 전체를 최신순으로 보여줍니다.</p>
          </div>

          <div className="customer-leads-table-scroll">
            <table className="customer-leads-table">
              <thead>
                <tr>
                  <th>등록일</th>
                  <th>연락처</th>
                  <th>고객</th>
                  <th>유입</th>
                  <th>안내할 가격</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map((row) => {
                  const utmText = [row.utm_source, row.utm_campaign, row.utm_content].filter(Boolean).join(' / ');
                  const priceContext = getLeadPriceContext(row);
                  return (
                    <tr key={row.id}>
                      <td>{formatDateTime(row.created_at)}</td>
                      <td className="customer-leads-contact">
                        {row.phone ? <a href={`tel:${row.phone}`}>{row.phone}</a> : <span>-</span>}
                        {row.email ? <a href={`mailto:${row.email}`}>{row.email}</a> : <span>-</span>}
                      </td>
                      <td>
                        <strong>{row.name || '-'}</strong>
                        <span>{row.organization || '-'}</span>
                      </td>
                      <td>
                        <span className="customer-leads-chip">{row.display_source || getSourceLabel(row.source)}</span>
                      </td>
                      <td className="customer-leads-price">
                        <strong>{priceContext.price || '가격 정보 없음'}</strong>
                        <span>{priceContext.headline || row.selected_plan || row.landing_page || row.display_source || '신청 맥락 없음'}</span>
                        <em>{priceContext.detail || utmText || row.notes || ''}</em>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="5" className="customer-leads-empty">조건에 맞는 리드가 없어요.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CustomerLeadsPage;
